import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getValidToken, API_URL } from './utils/authUtils';
import AdminRouteGuard from './components/AdminRouteGuard';
import { ADMIN_SECTIONS } from './utils/adminPermissions';

const BACKEND_URL = API_URL;

const ActivationTokensPage = () => {
  const navigate = useNavigate();
  const [activationTokens, setActivationTokens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [revokingTokens, setRevokingTokens] = useState(new Set());

  useEffect(() => {
    fetchActivationTokens();
  }, []);

  const fetchActivationTokens = async () => {
    setLoading(true);
    try {
      const token = getValidToken();
      if (!token) {
        setError('Your session has expired. Please log in again.');
        setTimeout(() => navigate('/admin'), 2000);
        return;
      }

      const response = await axios.get(`${BACKEND_URL}/api/admin/activation-tokens`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      setActivationTokens(response.data || []);
    } catch (err) {
      console.error('Error fetching activation tokens:', err);
      setError(err.response?.data?.detail || 'Failed to fetch activation tokens');
    } finally {
      setLoading(false);
    }
  };

  const revokeActivationToken = async (tokenId) => {
    setRevokingTokens(prev => new Set(prev).add(tokenId));
    try {
      const token = getValidToken();
      if (!token) {
        setError('Your session has expired. Please log in again.');
        setTimeout(() => navigate('/admin'), 2000);
        return;
      }

      await axios.post(
        `${BACKEND_URL}/api/admin/revoke-activation-token`,
        { token_id: tokenId },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      // Refresh the list
      fetchActivationTokens();
      setError({ type: 'success', message: 'Activation token revoked successfully' });
    } catch (err) {
      console.error('Error revoking activation token:', err);
      setError({ type: 'error', message: err.response?.data?.detail || 'Failed to revoke activation token' });
    } finally {
      setRevokingTokens(prev => {
        const newSet = new Set(prev);
        newSet.delete(tokenId);
        return newSet;
      });
    }
  };

  const copyToClipboard = (text, type) => {
    navigator.clipboard.writeText(text).then(() => {
      setError({ type: 'success', message: `${type} copied to clipboard!` });
      setTimeout(() => setError(''), 3000);
    }).catch(() => {
      setError({ type: 'error', message: `Failed to copy ${type.toLowerCase()}` });
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white dark:from-gray-900 dark:to-gray-800 p-8">
        <div className="max-w-7xl mx-auto">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
            <div className="animate-pulse">
              <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/4 mb-4"></div>
              <div className="space-y-3">
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-5/6"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <AdminRouteGuard requiredPermission={ADMIN_SECTIONS.PARTNER_TOKENS}>
      <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white dark:from-gray-900 dark:to-gray-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                Partner Activation Tokens
              </h1>
              <p className="mt-2 text-gray-600 dark:text-gray-300">
                Manage university partner account activation links
              </p>
              <p className="mt-1 text-sm text-blue-600 dark:text-blue-400">
                💡 Generate new tokens from the Partner Account Management tab
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => navigate('/admin')}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors flex items-center gap-2 shadow-sm"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2 2v0z M3 7l9-6 9 6" />
                </svg>
                Dashboard
              </button>
              <button
                onClick={() => navigate('/admin?tab=partner-accounts')}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 shadow-sm"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                Back to Partner Management
              </button>
            </div>
          </div>
        </div>

        {/* Error/Success Messages */}
        {error && (
          <div className={`mb-6 p-4 rounded-md ${
            typeof error === 'object' && error.type === 'success' 
              ? 'bg-green-100 text-green-700 border border-green-200' 
              : 'bg-red-100 text-red-700 border border-red-200'
          }`}>
            {typeof error === 'object' ? error.message : error}
          </div>
        )}

        {/* Tokens List */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
          <div className="p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                Active Activation Tokens ({activationTokens.length})
              </h2>
              <button
                onClick={fetchActivationTokens}
                className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-colors"
              >
                Refresh
              </button>
            </div>
            
            {activationTokens.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-500 dark:text-gray-400">
                  No active activation tokens found.
                </p>
                <p className="text-sm text-gray-400 dark:text-gray-500 mt-2">
                  Generate activation tokens from the Partner Account Management section.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {activationTokens.map((tokenData) => (
                  <div
                    key={tokenData.id}
                    className="border dark:border-gray-700 rounded-lg p-4 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="font-medium text-gray-900 dark:text-white">
                            {tokenData.partner_name}
                          </h3>
                          {tokenData.level && (
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              tokenData.level === 'Primary'
                                ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                                : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                            }`}>
                              {tokenData.level}
                            </span>
                          )}
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            new Date(tokenData.expires_at) < new Date() 
                              ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' 
                              : 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                          }`}>
                            {new Date(tokenData.expires_at) < new Date() ? 'Expired' : 'Active'}
                          </span>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm text-gray-600 dark:text-gray-300">
                          <p><strong>University:</strong> {tokenData.university}</p>
                          <p><strong>Partner Email:</strong> {tokenData.partner_email}</p>
                          <p><strong>Level:</strong> {tokenData.level || 'Not specified'}</p>
                          <p><strong>Generated:</strong> {new Date(tokenData.created_at).toLocaleDateString()}</p>
                          <p><strong>Expires:</strong> {new Date(tokenData.expires_at).toLocaleDateString()}</p>
                          
                          <div className="md:col-span-3 mt-2">
                            <p className="text-xs break-all mb-1">
                              <strong>Activation URL:</strong>
                            </p>
                            <code className="block bg-gray-100 dark:bg-gray-800 p-2 rounded text-xs break-all">
                              {tokenData.activation_url}
                            </code>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex flex-col space-y-2 ml-4">
                        <button
                          onClick={() => copyToClipboard(tokenData.activation_url, 'Activation URL')}
                          className="px-3 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 transition-colors"
                        >
                          Copy URL
                        </button>
                        
                        {new Date(tokenData.expires_at) > new Date() && (
                          <button
                            onClick={() => {
                              if (window.confirm(`Are you sure you want to revoke the activation token for ${tokenData.partner_name}? This action cannot be undone.`)) {
                                revokeActivationToken(tokenData.id);
                              }
                            }}
                            disabled={revokingTokens.has(tokenData.id)}
                            className={`px-3 py-1 text-xs rounded transition-colors ${
                              revokingTokens.has(tokenData.id)
                                ? 'bg-gray-400 text-gray-700 cursor-not-allowed'
                                : 'bg-red-600 text-white hover:bg-red-700'
                            }`}
                          >
                            {revokingTokens.has(tokenData.id) ? 'Revoking...' : 'Revoke'}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      </div>
    </AdminRouteGuard>
  );
};

export default ActivationTokensPage;